import './Home.scss';
import {
  useEffect,
  useState
} from 'react';
import { Icon, ErrorBoundary, ErrorDisplayComponent, ResponsiveGrid, DealerMaskMessage, ContentBlock, ContentBlockSkeleton } from '@gplibraries/react/components';
import Announcements from '../../components/Announcements/Announcements.jsx';
import AppTile from '../../components/AppTile/AppTile';
import AppTileSkeleton from '../../components/AppTile/AppTileSkeleton';
import BulletinsTable from '../../components/BulletinsTable/BulletinsTable.jsx';
import ResourceTabs from '../../components/ResourceTabs/ResourceTabs.jsx'
import { Link } from 'react-router';
import { useConfig, useTokenProvider, useAuthorizer, useLogger, useDealerInfo } from '@gplibraries/react/hooks';
import { useQuery } from '@tanstack/react-query';
import { fetchRecentBulletins } from '../../api/bulletins.js';
import { fetchSingleEmployeeUser } from "../../api/employeeUserManagement.js";

export default function Home() {

  const logger = useLogger();
  const appTilesConfig = useConfig('appTilesConfig');
  const { dealerAccessApiScope, dealerAccessApiHost } = useConfig();
  const getToken = useTokenProvider({ scopes: [dealerAccessApiScope]});
  const [userRoles, setUserRoles] = useState([]);
  const { data: dealerInfo } = useDealerInfo();

  // middleware token query
  const { data: token, isLoading: tokenLoading } = useQuery({
    enabled: Boolean(getToken),
    queryKey: ['middlewareToken'],
    queryFn: () => getToken()
  });
  const user = useAuthorizer(token?.accessTokenClaims);

  const { isLoading: bulletinsLoading, error: bulletinsError, data: bulletins } = useQuery({
    enabled: Boolean(getToken && dealerAccessApiHost),
    queryKey: ['recent_bulletins'],
    queryFn: () => getToken()
      .then(token => fetchRecentBulletins(dealerAccessApiHost, token.accessToken, 8))
      .then(res => res.json())
  })

  const { isLoading: euLoading, data: employeeUser } = useQuery({
    enabled: (Boolean(getToken) && Boolean(dealerInfo?.dealer_number) && user?.isDealerEmployeeUser()),
    queryKey: ['employee_user', dealerInfo?.dealer_number, user?.get('id')],
    queryFn: () => getToken()
      .then(token => fetchSingleEmployeeUser(dealerAccessApiHost, token.accessToken, dealerInfo?.dealer_number, user?.get('id')))
      .then(res => res.json()),
    select: (data) => data?.data
  })

  useEffect(() => {
    const baseRoles = user?.get('roles') ?? [];
    const employeeRoles = employeeUser?.permissions?.find(perms => perms.division === '01')?.values ?? [];
    const nextRoles = [...new Set([...baseRoles, ...employeeRoles])];
    setUserRoles(prev => {
      if (prev.length === nextRoles.length && prev.every(role => nextRoles.includes(role))) {
        return prev;
      }
      return nextRoles;
    });
  }, [user, employeeUser]);

  const loading = (bulletinsLoading || euLoading || tokenLoading)

  return (
    <div className="home-page-container flex-col">

      <DealerMaskMessage/>

      <Announcements/>

      <ResponsiveGrid className={'center app-tiles-grid'} columnWidth={appTilesConfig.appTileSize} gap={'1.5rem'}>

        {loading && Array.from({ length: 8 })
          .map((_, index) => <AppTileSkeleton key={'skeleton-'+index}/>)
        }

        {!loading && appTilesConfig.tiles
          .filter(tile =>
            (tile?.allowedRoles?.length && tile?.allowedRoles?.some(r => userRoles.includes(r))) ||
            (tile?.allowedGroups?.length && tile?.allowedGroups?.some(g => user?.hasGroup(g))) ||
            (!tile?.allowedRoles?.length && !tile?.allowedGroups?.length)
          )
          .map(tile =>
            <AppTile
              key={tile.title}
              href={tile.href}
              title={tile.title}
              icon={<Icon glyph={tile.icon?.glyph ?? ''} src={tile.icon?.src ?? ''} />}
              external={tile.external || false}
            />
        )}

      </ResponsiveGrid>

      {loading && <ContentBlockSkeleton hasTitle={true}/>}
      {!loading &&
        <ContentBlock title={'Recent Bulletins'} icon={<Icon glyph={'pin-angle-fill'}/>}>
          <ErrorBoundary
            fallback={<ErrorDisplayComponent message={'Recent Bulletins could not be loaded.'}/>}
            logger={logger}
          >

            <BulletinsTable
              bulletins={bulletins}
              loading={loading}
              error={bulletinsError}
              hideSort={true}
              hideFilters={true}
              hideFields={['bulletin_id', 'archived']}
            />
            {(bulletins && !loading) &&
              <div className="flex-row full right">
                <Link to={'/bulletins'}>
                  View All Bulletins<Icon glyph={'caret-right-fill'}/>
                </Link>
              </div>
            }

          </ErrorBoundary>
        </ContentBlock>
      }

      {loading && <ContentBlockSkeleton hasTitle={true}/>}
      {!loading && (user?.isTm() || user?.isCsUser() || user?.isAdmin()) &&
        <ContentBlock title={'TM Resources'} loading={loading} icon={<Icon glyph={'briefcase-fill'}/>}>
          <ErrorBoundary
            fallback={<ErrorDisplayComponent message={'TM resources could not be loaded.'} />}
            logger={logger}
          >
            <ResourceTabs type={'tm'}/>
          </ErrorBoundary>
        </ContentBlock>
      }

      {loading && <ContentBlockSkeleton hasTitle={true}/>}
      {!loading &&
        <ContentBlock title={'Dealer Resources'} loading={loading} icon={<Icon glyph={'file-earmark-text-fill'} />} >
          <ErrorBoundary
            fallback={<ErrorDisplayComponent message={'Dealer resources could not be loaded.'} />}
            logger={logger}
          >
            <ResourceTabs type={'dealers'}/>
          </ErrorBoundary>
        </ContentBlock>
      }

    </div>
  );

}